import type { IntRange, Sum, UnionToTuple } from 'type-fest'
import { z } from 'zod'

import { clamp } from '@mntn-dev/utilities'
import type {
  MaxValue,
  ObjectValuesToTuple,
  SumValues,
  ZodInfer,
} from '@mntn-dev/utility-types'

/**
 * Schemas and types for Video Professional Application (bid) Rating
 *
 * This module implements a comprehensive scoring system for evaluating video professional bids
 * based on various criteria including experience, quality, certifications, and
 * project alignment. The scoring system helps brands identify the best video professionals
 * for their projects.
 *
 * Reference: https://docs.google.com/spreadsheets/d/1qJsJpgjszt6gyxbX5RG-gTm2AkyOStWkVj4CkxIeUiM/edit?gid=0#gid=0
 */

/**
 * Rating Category Score (Experience/Quality)
 *
 * Converts numerical ratings (0-5 scale) into discrete scoring categories:
 *  3: Excellent (4.9+) - Top tier video professionals with outstanding ratings
 *  2: Good (4.5 - 4.89) - High quality video professionals with strong track records
 *  1: Average (4 - 4.49) - Decent video professionals with acceptable performance
 *  0: No rating - New video professionals or those without sufficient feedback
 * -1: Poor (Below 4) - video professionals with concerning performance history
 */
export const RatingCategoryScoreSchema = z.union([
  z.literal(3),
  z.literal(2),
  z.literal(1),
  z.literal(0),
  z.literal(-1),
])

export type RatingCategoryScore = ZodInfer<typeof RatingCategoryScoreSchema>

// Input schema for raw rating values (0-5 scale from user feedback)
export const RatingCategoryScoreInputSchema = z.number().min(0).max(5)

export type RatingCategoryScoreInput = ZodInfer<
  typeof RatingCategoryScoreInputSchema
>

/**
 * Converts a raw rating (0-5 scale) to a categorical score.
 *
 * @param input - Raw rating value from user feedback (default: 0)
 * @returns Categorical score based on rating thresholds
 */
export const toRatingCategoryScore = (input: RatingCategoryScoreInput = 0) =>
  RatingCategoryScoreSchema.parse(
    [
      { min: 4.9, score: 3 },
      { min: 4.5, score: 2 },
      { min: 4.0, score: 1 },
    ].find((threshold) => input >= threshold.min)?.score ?? -1
  )

/**
 * Count Category Score (Projects Rated/Projects Completed)
 *
 * Evaluates video professional experience based on project volume:
 *  2: Experienced (3+ projects) - Proven track record with multiple projects
 *  1: Some experience (1-2 projects) - Limited but existing experience
 *  0: No experience (0 projects) - New to the platform or category
 */
export const CountCategoryScoreSchema = z.union([
  z.literal(2),
  z.literal(1),
  z.literal(0),
])

export type CountCategoryScore = ZodInfer<typeof CountCategoryScoreSchema>

// Input schema for project count values
export const CountCategoryScoreInputSchema = z.number().nonnegative()

export type CountCategoryScoreInput = ZodInfer<
  typeof CountCategoryScoreInputSchema
>

/**
 * Converts a project count to a categorical score
 *
 * @param input - Number of projects (rated or completed)
 * @returns Categorical score based on experience thresholds
 */
export const toCountCategoryScore = (
  input: CountCategoryScoreInput = 0
): CountCategoryScore =>
  CountCategoryScoreSchema.parse(
    [
      { min: 3, score: 2 },
      { min: 1, score: 1 },
    ].find((threshold) => input >= threshold.min)?.score ?? 0
  )

/**
 * High Value Category Score
 *
 * Binary scoring for high-impact criteria (worth 3 points):
 *  3: Criteria met - Significant positive factor
 *  0: Criteria not met - No bonus points
 *
 * Used for: budget alignment, location match, tier 1 status, certifications, skill alignment, language match
 */
export const HighValueCategoryScoreSchema = z.union([
  z.literal(3),
  z.literal(0),
])

export type HighValueCategoryScore = ZodInfer<
  typeof HighValueCategoryScoreSchema
>

// Input schema for boolean criteria
export const HighValueCategoryScoreInputSchema = z.boolean()

export type HighValueCategoryScoreInput = ZodInfer<
  typeof HighValueCategoryScoreInputSchema
>

/**
 * Converts a boolean input to a high-value categorical score
 *
 * @param input - Boolean indicating if criteria is met
 * @returns 3 points if true, 0 if false
 */
export const toHighValueCategoryScore = (
  input: HighValueCategoryScoreInput = false
) => HighValueCategoryScoreSchema.parse(input ? 3 : 0)

/**
 * Medium Value Category Score
 *
 * Binary scoring for moderate-impact criteria (worth 2 points):
 *  2: Criteria met - Moderate positive factor
 *  0: Criteria not met - No bonus points
 *
 * Used for: platform tag alignment, relevant portfolio examples
 */
export const MediumValueCategoryScoreSchema = z.union([
  z.literal(2),
  z.literal(0),
])

export type MediumValueCategoryScore = ZodInfer<
  typeof MediumValueCategoryScoreSchema
>

// Input schema for boolean criteria
export const MediumValueCategoryScoreInputSchema = z.boolean()

export type MediumValueCategoryScoreInput = ZodInfer<
  typeof MediumValueCategoryScoreInputSchema
>

/**
 * Converts a boolean input to a medium-value categorical score
 *
 * @param input - Boolean indicating if criteria is met
 * @returns 2 points if true, 0 if false
 */
export const toMediumValueCategoryScore = (
  input: MediumValueCategoryScoreInput = false
) => MediumValueCategoryScoreSchema.parse(input ? 2 : 0)

/**
 * Low Value Category Score
 *
 * Binary scoring for low-impact criteria (worth 1 point):
 *  1: Criteria met - Minor positive factor
 *  0: Criteria not met - No bonus points
 *
 * Used for: industry tag alignment, similar past budget experience
 */
export const LowValueCategoryScoreSchema = z.union([z.literal(1), z.literal(0)])

export type LowValueCategoryScore = ZodInfer<typeof LowValueCategoryScoreSchema>

// Input schema for boolean criteria
export const LowValueCategoryScoreInputSchema = z.boolean()

export type LowValueCategoryScoreInput = ZodInfer<
  typeof LowValueCategoryScoreInputSchema
>

/**
 * Converts a boolean input to a low-value categorical score
 *
 * @param input - Boolean indicating if criteria is met
 * @returns 1 point if true, 0 if false
 */
export const toLowValueCategoryScore = (
  input: LowValueCategoryScoreInput = false
) => LowValueCategoryScoreSchema.parse(input ? 1 : 0)

/**
 * Scored Bid Model
 *
 * Complete scoring model for evaluating video professional bids across all criteria
 * including performance metrics, project alignment, certifications & status.
 */
export const ScoredBidModelSchema = z.object({
  /**
   * Performance Metrics:
   * - experienceRating: Rating from previous customers about their experience with the video pro
   * - qualityRating: Rating from previous customers about the quality of the services rendered
   * - projectsRated: The number of projects that a video pro has a rating for
   * - projectsCompleted: The number of projects that a video pro has completed
   */
  experienceRating: RatingCategoryScoreSchema,
  qualityRating: RatingCategoryScoreSchema,
  projectsRated: CountCategoryScoreSchema,
  projectsCompleted: CountCategoryScoreSchema,

  /**
   * Project Alignment:
   * - inBudget: Did the video pro apply with a budget at or below the customers maximum budget?
   * - inLocation: If the project needs to happen in a specific location, is the video pro located in the area?
   * - meetsIndustryTags: Has the video pro worked with another customer in this business vertical?
   * - meetsPlatformTags: Has the video pro created content for the same platform where this customer's work will run?
   * - meetsSkillTags: Does the video pro have the necessary skills tagged to accomplish the needs of the project?
   * - meetsLanguageTags: Does the video pro speak the language that the content needs to be produced in?
   * - similarPastBudget: Does the video pro have experience working on projects with similar budgets?
   * - relevantExamples: Does the video pro have examples that are relevant to the needs and goals of the project?
   */
  inBudget: HighValueCategoryScoreSchema, // Not implemented
  inLocation: HighValueCategoryScoreSchema,
  meetsIndustryTags: LowValueCategoryScoreSchema,
  meetsPlatformTags: MediumValueCategoryScoreSchema,
  meetsSkillTags: HighValueCategoryScoreSchema,
  meetsLanguageTags: HighValueCategoryScoreSchema,
  similarPastBudget: LowValueCategoryScoreSchema,
  relevantExamples: MediumValueCategoryScoreSchema, // Not implemented

  /**
   * Certifications & Status:
   * - tier1Status: If the project needs a Tier 1 video pro, is the video pro marked as being tier 1?
   * - generalQfCertified: Has the video pro completed the latest version of the General QuickFrame onboarding course?
   * - mntnCertified: If the project needs a MNTN Certified video pro, is the video pro marked as being MNTN certified?
   * - tikTokCertified: If the project needs a TikTok Certified video pro, is the video pro marked as being TikTok certified?
   * - metaCertified: If the project needs a Meta Certified video pro, is the video pro marked as being Meta certified?
   */
  tier1Status: HighValueCategoryScoreSchema,
  generalQfCertified: HighValueCategoryScoreSchema,
  mntnCertified: HighValueCategoryScoreSchema,
  tikTokCertified: HighValueCategoryScoreSchema,
  metaCertified: HighValueCategoryScoreSchema,
})

export type ScoredBidModel = ZodInfer<typeof ScoredBidModelSchema>

/**
 * Helper type to convert a union of numbers to a tuple of numbers
 */
type NumberUnionToTuple<T extends number> =
  UnionToTuple<T> extends infer U
    ? U extends readonly number[]
      ? U
      : never
    : never

/**
 * Maximum possible bid score
 *
 * Calculated by summing the maximum values from each scoring category.
 * This represents a perfect score where a video professional meets all criteria
 * at the highest level.
 */
export type MaxBidScore = SumValues<
  ObjectValuesToTuple<{
    [K in keyof ScoredBidModel]: MaxValue<NumberUnionToTuple<ScoredBidModel[K]>>
  }>
>

// Calculate the actual maximum score value at compile time
export const MaxBidScore = ScoredBidModelSchema.keyof().options.reduce<number>(
  (score, category) =>
    score +
    Math.max(
      ...ScoredBidModelSchema.shape[category].options.map(
        (option) => option.value
      )
    ),
  0
) as MaxBidScore

/**
 * Bid Score
 *
 * Final numerical score for a bid, constrained to be between 0 and MaxBidScore.
 * Higher scores indicate better alignment with project requirements and
 * stronger video professional credentials.
 */
export const BidScoreSchema = z
  .int()
  .nonnegative()
  .refine(
    // If this numeric range type proves too difficult to work with, we can just use
    // .max(MaxBidScore) instead and have the bid score just be a nonnegative number.
    (score): score is IntRange<0, Sum<1, MaxBidScore>> => score <= MaxBidScore
  )

export type BidScore = ZodInfer<typeof BidScoreSchema>

// the interview score threshold designating a maker as exceptional
export const NEW_MAKER_EXCEPTIONAL_THRESHOLD_PERCENT = 90
export const NEW_MAKER_EXCEPTIONAL_PLACEHOLDER_SCORE = 5
export const NEW_MAKER_GENERAL_PLACEHOLDER_SCORE = 4

// the bid score penalty to apply for each active project the maker currently has outstanding
export const PER_ACTIVE_PROJECT_PENALTY = 1

/**
 * New Maker Rating
 *
 * Classification for makers that have no reviewed, completed projects.
 * These are derived from the maker's organization interview results
 * - "exceptional": maker scored NEW_MAKER_EXCEPTIONAL_THRESHOLD_PERCENT or higher
 * - "general": maker scored below NEW_MAKER_EXCEPTIONAL_THRESHOLD_PERCENT
 */
export const NewMakerRatingSchema = z.enum(['exceptional', 'general'])

export type NewMakerRating = ZodInfer<typeof NewMakerRatingSchema>

/**
 * Bid Scorecard Input Model
 *
 * Input schema for raw data used to calculate bid scores.
 * All fields are optional to handle cases where data may be incomplete.
 * Uses raw input types (numbers, booleans) that get converted to
 * categorical scores during evaluation.
 */
export const BidScorecardModelSchema = z
  .object({
    // Rating Categories
    experienceRating: RatingCategoryScoreInputSchema,
    qualityRating: RatingCategoryScoreInputSchema,
    // Count Categories
    projectsRated: CountCategoryScoreInputSchema,
    projectsCompleted: CountCategoryScoreInputSchema,
    // High Value Categories
    inBudget: HighValueCategoryScoreInputSchema,
    inLocation: HighValueCategoryScoreInputSchema,
    tier1Status: HighValueCategoryScoreInputSchema,
    generalQfCertified: HighValueCategoryScoreInputSchema,
    mntnCertified: HighValueCategoryScoreInputSchema,
    tikTokCertified: HighValueCategoryScoreInputSchema,
    metaCertified: HighValueCategoryScoreInputSchema,
    meetsSkillTags: HighValueCategoryScoreInputSchema,
    meetsLanguageTags: HighValueCategoryScoreInputSchema,
    // Medium Value Categories
    meetsPlatformTags: MediumValueCategoryScoreInputSchema,
    relevantExamples: MediumValueCategoryScoreInputSchema,
    // Low Value Categories
    meetsIndustryTags: LowValueCategoryScoreInputSchema,
    similarPastBudget: LowValueCategoryScoreInputSchema,
    activeProjectPenalty: z.number().nonnegative(),
  })
  .partial()

export type BidScorecardModel = ZodInfer<typeof BidScorecardModelSchema>

/**
 * Evaluates a bid scorecard and returns the total score
 *
 * Takes raw input data and converts each field to its appropriate
 * categorical score, then sums all scores to get the final bid score.
 * Missing fields default to 0 points.
 *
 * @param scorecard - Raw scorecard data with optional fields
 * @returns Total bid score (0 to MaxBidScore)
 *
 * @example
 * ```typescript
 * const score = calculateBidScore({
 *   experienceRating: 4.8,
 *   inBudget: true,
 *   meetsSkillTags: true,
 *   // ... other criteria
 * });
 * // Returns numerical score based on weighted criteria
 * ```
 */
export const calculateBidScore = (scorecard: BidScorecardModel) => {
  const sum = (a: number, b: number) => a + b

  const rawTotal = [
    // Rating Categories
    toRatingCategoryScore(scorecard.experienceRating),
    toRatingCategoryScore(scorecard.qualityRating),
    // Count Categories
    toCountCategoryScore(scorecard.projectsRated),
    toCountCategoryScore(scorecard.projectsCompleted),
    // High Value Categories
    toHighValueCategoryScore(scorecard.inBudget),
    toHighValueCategoryScore(scorecard.inLocation),
    toHighValueCategoryScore(scorecard.tier1Status),
    toHighValueCategoryScore(scorecard.generalQfCertified),
    toHighValueCategoryScore(scorecard.mntnCertified),
    toHighValueCategoryScore(scorecard.tikTokCertified),
    toHighValueCategoryScore(scorecard.metaCertified),
    toHighValueCategoryScore(scorecard.meetsSkillTags),
    toHighValueCategoryScore(scorecard.meetsLanguageTags),
    // Medium Value Categories
    toMediumValueCategoryScore(scorecard.meetsPlatformTags),
    toMediumValueCategoryScore(scorecard.relevantExamples),
    // Low Value Categories
    toLowValueCategoryScore(scorecard.meetsIndustryTags),
    toLowValueCategoryScore(scorecard.similarPastBudget),
  ].reduce(sum, 0)

  return BidScoreSchema.parse(
    clamp(rawTotal - (scorecard.activeProjectPenalty ?? 0), 0, MaxBidScore)
  )
}
